import { useState } from 'react';
import { IconButton, Link } from '@mui/material';
import { Close } from '@mui/icons-material';
import Mship from '../../pages/sc/mships/Mship';
import userImage from '../../images/user.svg';
import { SocketMessage } from '../../types/common';
import { safeFormatDate } from '../../lib/utils';

const accessTypes = {
    BOOKING: 'BOOKING',
    CLASS: 'ACTIVITY',
    GROUP: 'GROUP',
    BONO: 'BONO',
} as const;

interface UserAccessPopupProps {
    visible: boolean;
    messages: SocketMessage[];
    onDelete: (message: SocketMessage) => void;
}

/**
 * Componente que pinta los accesos de usuarios según van entrando por el torno, puerta, etc.
 * @param visible
 * @param messages array de mensajes de entrada (se van eliminando solos cada 10 segundos)
 * @param onDelete
 * @returns {React.ReactElement|null}
 */
export default function UserAccessPopup({
    visible,
    messages,
    onDelete,
}: UserAccessPopupProps) {
    const [mshipOpen, setMshipOpen] = useState<number | null>(null);

    function accessTypeString(access?: SocketMessage['access']): string {
        if (!access) {
            return '';
        }

        switch (access?.type) {
            case accessTypes.BOOKING:
                return `${access?.booking?.field?.name || ''} ${safeFormatDate(access?.booking?.ini, 'HH:mm')} - ${safeFormatDate(access?.booking?.end, 'HH:mm')}`;
            case accessTypes.GROUP:
                return `${access?.group?.name || ''}`;
            case accessTypes.CLASS:
                return `${access?.activity?.name || ''} ${safeFormatDate(access?.activity?.ini, 'HH:mm')} - ${safeFormatDate(access?.activity?.end, 'HH:mm')}`;
            case accessTypes.BONO:
                return `${access?.bono?.name || ''} (${access?.bono?.consumed} / ${access?.bono?.amount})`;
            default:
                return 'Acceso concedido';
        }
    }

    if (visible || mshipOpen) {
        return mshipOpen ? (
            <Mship
                open
                id={mshipOpen}
                onClose={() => setMshipOpen(null)}
                disableUrlNavigation
            />
        ) : (
            <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
                {messages.map((message: SocketMessage, i: number) => (
                    <div
                        key={i}
                        className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-lg"
                        style={{ minWidth: 320 }}
                    >
                        <img
                            src={message?.mship?.img?.url || userImage}
                            alt=""
                            className="h-12 w-12 rounded-full object-cover"
                        />
                        <div className="flex-1">
                            <Link
                                onClick={() => {
                                    const mshipId = message?.mship?.id;
                                    if (mshipId) {
                                        setMshipOpen(
                                            typeof mshipId === 'string'
                                                ? parseInt(mshipId, 10)
                                                : mshipId,
                                        );
                                    }
                                }}
                                className="text-brand-text cursor-pointer text-lg no-underline hover:underline"
                            >
                                {message?.mship?.fullName || ''}
                            </Link>
                            <div className="text-sm text-gray-700">
                                {accessTypeString(message?.access)}
                            </div>
                            <div className="text-sm text-gray-500">
                                {message?.device?.name || ''}
                            </div>
                        </div>
                        <IconButton
                            onClick={() => onDelete(message)}
                            size="small"
                        >
                            <Close />
                        </IconButton>
                    </div>
                ))}
            </div>
        );
    }

    return null;
}
